import React, { useRef, useState, useEffect, useCallback } from "react";
import { Typography } from "@mui/material";
import "./videoRecorder.scss";
import closeIcon from "../../assets/images/closeIcon.svg";
import { useSection } from "../../i18n/useSection";
import { isWebView, postWebViewMessage } from "../../utlis/webViewBridge";
import { resolveVideoOrientation } from "./videoOrientation";

const VideoRecorder = ({
  onRecordingComplete,
  onCancel,
  facingMode = "environment", // 'user' for front camera, 'environment' for back camera
  maxDuration = 300, // 5 minutes default
  preferredOrientation = "portrait", // 'portrait' or 'landscape'
  countdownSeconds = 3, // Countdown before recording starts
}) => {
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const t = useSection("videoRecorder");

  const [recordingState, setRecordingState] = useState("idle");
  // States: idle, requesting, previewing, countdown, recording, stopped, error
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [error, setError] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [countdown, setCountdown] = useState(countdownSeconds);
  const [orientation, setOrientation] = useState(() =>
    resolveVideoOrientation({
      embeddedInWebView: isWebView(),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      screenOrientationType: window.screen?.orientation?.type,
    }),
  );
  const [isOrientationCorrect, setIsOrientationCorrect] = useState(true);
  const [currentFacingMode, setCurrentFacingMode] = useState(facingMode);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [submittingRecording, setSubmittingRecording] = useState(false);
  const [recordingValidationError, setRecordingValidationError] = useState("");

  // Detect device orientation
  const detectOrientation = useCallback(() => {
    setOrientation(
      resolveVideoOrientation({
        embeddedInWebView: isWebView(),
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        screenOrientationType: window.screen?.orientation?.type,
      }),
    );
  }, []);

  // The Inochi native shell normally stays portrait. Ask it to rotate only
  // while a recorder that requires landscape is mounted, then restore portrait.
  useEffect(() => {
    if (!isWebView()) return undefined;

    postWebViewMessage("SET_ORIENTATION", {
      orientation: preferredOrientation,
    });

    return () => {
      postWebViewMessage("SET_ORIENTATION", { orientation: "portrait" });
    };
  }, [preferredOrientation]);

  // Check if orientation matches preferred
  useEffect(() => {
    setIsOrientationCorrect(orientation === preferredOrientation);
  }, [orientation, preferredOrientation]);

  // Listen for orientation changes
  useEffect(() => {
    detectOrientation();

    const handleOrientationChange = () => {
      detectOrientation();
    };

    if (window.screen && window.screen.orientation) {
      window.screen.orientation.addEventListener(
        "change",
        handleOrientationChange,
      );
    }
    window.addEventListener("resize", handleOrientationChange);
    window.addEventListener("orientationchange", handleOrientationChange);

    return () => {
      if (window.screen && window.screen.orientation) {
        window.screen.orientation.removeEventListener(
          "change",
          handleOrientationChange,
        );
      }
      window.removeEventListener("resize", handleOrientationChange);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, [detectOrientation]);

  // Check if device has multiple cameras
  const checkMultipleCameras = async () => {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(
          (device) => device.kind === "videoinput",
        );
        setHasMultipleCameras(videoDevices.length > 1);
      }
    } catch (err) {
      console.error("Error checking cameras:", err);
      setHasMultipleCameras(false);
    }
  };

  // Request camera access
  const requestCameraAccess = async (useFacingMode = currentFacingMode) => {
    setError(null);

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(t?.err_unsupported);
      }

      const constraints = {
        video: {
          facingMode: useFacingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 },
        },
        audio: false,
      };

      setRecordingState("requesting");

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Check for multiple cameras after getting permission
      checkMultipleCameras();

      setRecordingState("previewing");
    } catch (err) {
      console.error("Error accessing camera:", err);
      let errorMessage = t?.err_failed_access || "Failed to access camera. ";

      if (err.name === "NotAllowedError") {
        errorMessage += t?.err_not_allowed;
      } else if (err.name === "NotFoundError") {
        errorMessage += t?.err_not_found;
      } else if (err.name === "NotReadableError") {
        errorMessage += t?.err_not_readable;
      } else {
        errorMessage += err.message || t?.err_unknown;
      }

      setError(errorMessage);
      setRecordingState("error");
    }
  };

  // Switch between front and back camera
  const switchCamera = async () => {
    // Stop current stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Toggle facing mode
    const newFacingMode =
      currentFacingMode === "environment" ? "user" : "environment";
    setCurrentFacingMode(newFacingMode);

    // Request new camera
    await requestCameraAccess(newFacingMode);
  };

  // Start countdown before recording
  const startCountdown = () => {
    setCountdown(countdownSeconds);
    setRecordingState("countdown");

    let count = countdownSeconds;
    countdownIntervalRef.current = setInterval(() => {
      count -= 1;
      setCountdown(count);

      if (count <= 0) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
        actuallyStartRecording();
      }
    }, 1000);
  };

  // Actually start recording (after countdown)
  const actuallyStartRecording = () => {
    if (!streamRef.current) {
      setError(t?.err_no_stream);
      return;
    }

    try {
      chunksRef.current = [];

      // Prefer mp4 (H.264) so the report video is mp4 wherever the browser/
      // WebView supports it (iOS WebKit, modern Android Chromium). Fall back to
      // webm on engines that can't record mp4 (e.g. Firefox).
      const preferredMimeTypes = [
        "video/mp4;codecs=h264",
        "video/mp4",
        "video/webm;codecs=vp8,opus",
        "video/webm",
      ];

      const supportedMimeType =
        preferredMimeTypes.find((type) =>
          MediaRecorder.isTypeSupported(type),
        ) || "";

      const options = supportedMimeType
        ? { mimeType: supportedMimeType }
        : undefined;

      const mediaRecorder = new MediaRecorder(streamRef.current, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Use the recorder's actual mime type so the blob/file extension match
        // what was captured (mp4 or webm).
        const recordedType =
          mediaRecorder.mimeType || supportedMimeType || "video/webm";
        const blob = new Blob(chunksRef.current, { type: recordedType });
        setRecordedBlob(blob);

        const url = URL.createObjectURL(blob);
        setRecordedUrl(url);

        stopCameraStream();
        setRecordingState("stopped");
      };

      mediaRecorder.onerror = (event) => {
        console.error("MediaRecorder error:", event);
        setError(t?.err_recording_failed);
        setRecordingState("error");
      };

      mediaRecorder.start(100);
      setRecordingState("recording");
      setRecordingDuration(0);

      const startTime = Date.now();
      timerIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setRecordingDuration(elapsed);

        if (elapsed >= maxDuration) {
          stopRecording();
        }
      }, 1000);
    } catch (err) {
      console.error("Error starting recording:", err);
      setError((t?.err_start_recording_prefix || "") + err.message);
      setRecordingState("error");
    }
  };

  // Start recording (with countdown)
  const startRecording = () => {
    if (countdownSeconds > 0) {
      startCountdown();
    } else {
      actuallyStartRecording();
    }
  };

  // Cancel countdown
  const cancelCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setCountdown(countdownSeconds);
    setRecordingState("previewing");
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && recordingState === "recording") {
      mediaRecorderRef.current.stop();

      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
  };

  // Stop camera stream
  const stopCameraStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // Handle retake
  const handleRetake = () => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
      setRecordedUrl(null);
    }
    setRecordedBlob(null);
    setRecordingDuration(0);
    setRecordingValidationError("");
    requestCameraAccess();
  };

  // Handle use recording
  const handleUseRecording = async () => {
    if (recordedBlob && onRecordingComplete) {
      const extension = recordedBlob.type.includes("mp4") ? "mp4" : "webm";
      const file = new File(
        [recordedBlob],
        `recording_${Date.now()}.${extension}`,
        {
          type: recordedBlob.type,
        },
      );
      setRecordingValidationError("");
      setSubmittingRecording(true);
      try {
        const result = await onRecordingComplete(file);
        if (result?.valid === false) {
          setRecordingValidationError(
            result.message || t?.err_recording_failed,
          );
        }
      } catch {
        setRecordingValidationError(t?.err_recording_failed);
      } finally {
        setSubmittingRecording(false);
      }
    }
  };

  // Handle cancel
  const handleCancel = () => {
    cancelCountdown();
    stopRecording();
    stopCameraStream();

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }

    if (onCancel) {
      onCancel();
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Attach stream to video element when state changes to previewing
  useEffect(() => {
    if (
      (recordingState === "previewing" ||
        recordingState === "countdown" ||
        recordingState === "recording") &&
      streamRef.current &&
      videoRef.current
    ) {
      videoRef.current.srcObject = streamRef.current;

      videoRef.current.play().catch((playError) => {
        console.error("Error playing video:", playError);
      });
    }
  }, [recordingState]);

  // Initialize camera on mount
  useEffect(() => {
    requestCameraAccess();

    return () => {
      cancelCountdown();
      stopRecording();
      stopCameraStream();

      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }

      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
    };
  }, []);

  // Get title based on state
  const getTitle = () => {
    switch (recordingState) {
      case "stopped":
        return t?.title_preview;
      case "countdown":
        return t?.title_countdown;
      case "recording":
        return t?.title_recording;
      default:
        return t?.title_record;
    }
  };

  return (
    <div
      className={`video-recorder-wrapper ${preferredOrientation === "landscape" ? "video-recorder-landscape" : "video-recorder-portrait"} ${recordingState === "stopped" ? "video-recorder-review" : ""}`}
    >
      <div className="video-recorder-header">
        <Typography className="title">{getTitle()}</Typography>
        <img
          src={closeIcon}
          alt={t?.close}
          className="close-icon"
          onClick={handleCancel}
        />
      </div>

      <div className="video-recorder-content">
        {/* Error State */}
        {recordingState === "error" && (
          <div className="error-state">
            <Typography className="error-message">{error}</Typography>
            <button className="retry-btn" onClick={requestCameraAccess}>
              {t?.try_again}
            </button>
          </div>
        )}

        {/* Loading State */}
        {recordingState === "requesting" && (
          <div className="loading-state">
            <Typography className="loading-text">
              {t?.requesting_camera}
            </Typography>
          </div>
        )}

        {/* Camera Preview / Recording State */}
        {(recordingState === "previewing" ||
          recordingState === "countdown" ||
          recordingState === "recording") && (
          <div className="camera-preview">
            <video
              ref={videoRef}
              className={`video-preview ${currentFacingMode === "user" ? "mirror" : ""}`}
              autoPlay
              playsInline
              muted
            />

            {/* Switch Camera Button */}
            {hasMultipleCameras && recordingState === "previewing" && (
              <button
                className="switch-camera-btn"
                onClick={switchCamera}
                title={t?.switch_camera}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 5H16.83L15.59 3.65C15.22 3.24 14.68 3 14.12 3H9.88C9.32 3 8.78 3.24 8.4 3.65L7.17 5H4C2.9 5 2 5.9 2 7V19C2 20.1 2.9 21 4 21H20C21.1 21 22 20.1 22 19V7C22 5.9 21.1 5 20 5Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                  <circle
                    cx="12"
                    cy="13"
                    r="4"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M17 8L19 10M19 8L17 10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}

            {/* Orientation Warning */}
            {!isOrientationCorrect && recordingState === "previewing" && (
              <div className="orientation-warning">
                <div className="orientation-icon">
                  {preferredOrientation === "portrait" ? (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="6"
                        y="2"
                        width="12"
                        height="20"
                        rx="2"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <circle cx="12" cy="18" r="1" fill="currentColor" />
                    </svg>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="2"
                        y="6"
                        width="20"
                        height="12"
                        rx="2"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <circle cx="18" cy="12" r="1" fill="currentColor" />
                    </svg>
                  )}
                </div>
                <Typography className="orientation-text">
                  {preferredOrientation === "portrait"
                    ? t?.err_rotate_portrait
                    : t?.err_rotate_landscape}
                </Typography>
              </div>
            )}

            {/* Countdown Overlay */}
            {recordingState === "countdown" && (
              <div className="countdown-overlay">
                <div className="countdown-number">{countdown}</div>
                <Typography className="countdown-text">
                  {t?.title_countdown}
                </Typography>
                <button
                  className="cancel-countdown-btn"
                  onClick={cancelCountdown}
                >
                  {t?.cancel}
                </button>
              </div>
            )}

            {/* Recording Indicator */}
            {recordingState === "recording" && (
              <div className="recording-overlay">
                <div className="recording-indicator">
                  <span className="recording-dot"></span>
                  <Typography className="recording-text">{t?.rec}</Typography>
                </div>
                <div className="timer-display">
                  <Typography className="timer-current">
                    {formatTime(recordingDuration)}
                  </Typography>
                  <div className="timer-progress">
                    <div
                      className="timer-progress-bar"
                      style={{
                        width: `${(recordingDuration / maxDuration) * 100}%`,
                      }}
                    />
                  </div>
                  <Typography className="timer-max">
                    {formatTime(maxDuration)}
                  </Typography>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recorded Video Preview */}
        {recordingState === "stopped" && recordedUrl && (
          <div className="recorded-preview">
            <video
              ref={videoRef}
              src={recordedUrl}
              className="video-preview"
              playsInline
              controls
            />
            <div className="recording-info">
              <Typography className="info-text">
                {t?.duration_label} {formatTime(recordingDuration)}
              </Typography>
            </div>
            {recordingValidationError ? (
              <Typography
                className="recording-validation-error"
                role="alert"
                aria-live="assertive"
              >
                {recordingValidationError}
              </Typography>
            ) : null}
          </div>
        )}
      </div>

      {/* Controls */}
      <div
        className={`video-recorder-controls ${recordingState === "stopped" ? "is-review" : ""}`}
      >
        {recordingState === "previewing" && (
          <div className="recording-controls">
            <button className="cancel-btn" onClick={handleCancel}>
              {t?.cancel}
            </button>
            <button
              className="start-recording-btn"
              onClick={startRecording}
              disabled={!isOrientationCorrect}
            >
              <div className="record-button-inner"></div>
            </button>
            <div className="placeholder"></div>
          </div>
        )}

        {recordingState === "recording" && (
          <div className="recording-controls">
            <div className="placeholder"></div>
            <button className="stop-recording-btn" onClick={stopRecording}>
              <div className="stop-button-inner"></div>
            </button>
            <div className="placeholder"></div>
          </div>
        )}

        {recordingState === "stopped" && (
          <div className="preview-controls">
            <button className="retake-btn" onClick={handleRetake}>
              {t?.retake}
            </button>
            <button
              className="use-recording-btn"
              onClick={handleUseRecording}
              disabled={submittingRecording}
            >
              {t?.use_recording}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoRecorder;
